/**
 * Azure Web PubSub - Collaboration Service
 *
 * - Hub: collaboration
 * - Identity is derived from negotiate token (ce-userid)
 * - Mirrors NotificationsService architecture
 */

import { getMultiHubSocketService } from '../pubsub';

export type PresenceStatus = 'active' | 'inactive';

export type PresenceUpdate = {
  matterId: string;
  userId: string;
  userUID: string;
  status: PresenceStatus;
  ts?: string;
};

export type PresenceRoster = {
  matterId: string;
  roster: Record<string, PresenceStatus>;
  userUID: string;
  ts?: string;
};

export type CursorUpdate = {
  matterId: string;
  userId: string;
  userUID: string;
  cursor: any;
  ts?: string;
};

export type DocumentOp = {
  matterId: string;
  userId: string;
  userUID: string;
  opId?: string;
  ops: any;
  ts?: string;
};

export type JoinOkPayload = {
  matterId: string;
  group: string;
  roster?: Record<string, PresenceStatus>;
  ts?: string;
  userId?: string;
  userUID?: string;
  status?: PresenceStatus;
};

export type CollaborativeUser = {
  userId: string;
  status: PresenceStatus;
};

export type TableCellOp = {
  matterId: string;
  tableId: string;
  cellId: string;
  type: 'cell_edit' | 'cell_select' | 'cell_deselect' | 'cell_format';
  userId: string;
  userName?: string;
  value?: string;
  format?: Record<string, any>;
  opId?: string;
  ts?: string;
};

class PubSubCollaborationService {
  private pubsub = getMultiHubSocketService();
  private hub = 'collaboration';
  private listenersInitialized = false;

  private currentUserId: string | null = null;

  // State tracking
  private currentRoster: Record<string, PresenceStatus> = {};
  private currentGroup: string | null = null;

  // callbacks
  private joinOkCallbacks: Array<(d: JoinOkPayload) => void> = [];
  private joinDeniedCallbacks: Array<(d: { matterId: string; userUID?: string }) => void> = [];
  private presenceCallbacks: Array<(d: PresenceUpdate) => void> = [];
  private rosterCallbacks: Array<(d: PresenceRoster) => void> = [];
  private cursorCallbacks: Array<(d: CursorUpdate) => void> = [];
  private docOpCallbacks: Array<(d: DocumentOp) => void> = [];
  private tableCellOpCallbacks: Array<(d: TableCellOp) => void> = [];
  private connectionStatusCallbacks: Array<(connected: boolean) => void> = [];

  constructor(userId?: string) {
    if (userId) {
      this.currentUserId = String(userId);
      this.pubsub.setCurrentUserId(String(userId));
      console.log('[PubSubCollaborationService] Constructor called with userId:', userId);
    } else {
      console.log('[PubSubCollaborationService] Constructor called without userId');
    }
  }

  async initialize(userId: string): Promise<boolean> {
    try {
      this.currentUserId = String(userId);
      this.pubsub.setCurrentUserId(String(userId));

      console.log('[PubSubCollaborationService] Initializing with userId:', userId);
      const connected = await this.pubsub.connectToHub(this.hub);
      console.log('[PubSubCollaborationService] Connection to hub result:', connected);

      if (connected) {
        console.log('[PubSubCollaborationService] Successfully connected, setting up event listeners');
        this.setupEventListeners();
        this.notifyConnectionStatus(true);
      } else {
        console.warn('[PubSubCollaborationService] Failed to connect to hub');
      }
      return connected;
    } catch (err) {
      console.error('[PubSubCollaborationService] Initialization error:', err);
      return false;
    }
  }

  private _validateData(data: any, requiredFields: string[]): boolean {
    if (!data || typeof data !== 'object') {
      return false;
    }
    for (const field of requiredFields) {
      if (!(field in data)) {
        return false;
      }
    }
    return true;
  }

  private setupEventListeners(): void {
    if (this.listenersInitialized) {
      console.debug('[PubSubCollaborationService] Event listeners already initialized, skipping setup');
      return;
    }
    this.listenersInitialized = true;
    console.log('[PubSubCollaborationService] Setting up event listeners');

    this.pubsub.on(this.hub, '*', (data: any, eventName: string) => {
      try {
        // Skip null/undefined data
        if (!data) {
          console.debug('[PubSubCollaborationService] Received null/undefined data for event:', eventName);
          return;
        }

        console.log('[PubSubCollaborationService] Event received:', eventName, { dataIsArray: Array.isArray(data), dataKeys: Object.keys(data || {}) });

        switch (eventName) {
          case 'collab_join_ok': {
            console.log('[PubSubCollaborationService] Processing collab_join_ok event');
            // Handle both array and single object formats
            const joinEvents = Array.isArray(data) ? data : [data];
            console.debug('[PubSubCollaborationService] collab_join_ok - Number of events:', joinEvents.length);

            joinEvents.forEach((joinEvent: any, index: number) => {
              if (!this._validateData(joinEvent, ['group'])) {
                console.warn('[PubSubCollaborationService] collab_join_ok - Invalid event at index', index, '- missing required fields');
                return;
              }

              const joinData: JoinOkPayload = {
                matterId: joinEvent?.matterId || joinEvent?.MatterId || '',
                group: joinEvent.group,
                roster: (joinEvent?.roster && typeof joinEvent.roster === 'object') ? joinEvent.roster : {},
                ts: joinEvent?.ts,
                userId: joinEvent?.userId,
                userUID: joinEvent?.userUID || this.currentUserId || '',
                status: (joinEvent?.status || 'inactive') as PresenceStatus,
              };

              // console.log('[PubSubCollaborationService] collab_join_ok processed:', { matterId: joinData.matterId, group: joinData.group, rosterSize: Object.keys(joinData.roster).length, userUID: joinData.userUID });

              // Store current group and roster
              this.currentGroup = joinData.group;
              this.currentRoster = { ...joinData.roster };

              console.debug('[PubSubCollaborationService] collab_join_ok - Triggering', this.joinOkCallbacks.length, 'callbacks');
              this.joinOkCallbacks.forEach((cb, cbIndex) => {
                try {
                  cb(joinData);
                  console.debug('[PubSubCollaborationService] collab_join_ok - Callback', cbIndex, 'executed successfully');
                } catch (err) {
                  console.error('[PubSubCollaborationService] collab_join_ok - Callback', cbIndex, 'error:', err);
                }
              });
            });
            break;
          }

          case 'collab_join_denied': {
            console.log('[PubSubCollaborationService] Processing collab_join_denied event');
            // Handle both array and single object formats
            const denyEvents = Array.isArray(data) ? data : [data];
            console.debug('[PubSubCollaborationService] collab_join_denied - Number of events:', denyEvents.length);

            denyEvents.forEach((denyEvent: any, index: number) => {
              const denyData = {
                matterId: denyEvent?.matterId || denyEvent?.MatterId || '',
                userUID: denyEvent?.userUID || this.currentUserId,
              };
              console.log('[PubSubCollaborationService] collab_join_denied processed:', denyData);

              console.debug('[PubSubCollaborationService] collab_join_denied - Triggering', this.joinDeniedCallbacks.length, 'callbacks');
              this.joinDeniedCallbacks.forEach((cb, cbIndex) => {
                try {
                  cb(denyData);
                  console.debug('[PubSubCollaborationService] collab_join_denied - Callback', cbIndex, 'executed successfully');
                } catch (err) {
                  console.error('[PubSubCollaborationService] collab_join_denied - Callback', cbIndex, 'error:', err);
                }
              });
            });
            break;
          }

          case 'presence_update': {
            console.log('[PubSubCollaborationService] Processing presence_update event');
            // Handle both array and single object formats
            const updates = Array.isArray(data) ? data : [data];
            console.debug('[PubSubCollaborationService] presence_update - Number of events:', updates.length);

            updates.forEach((update: any, index: number) => {
              if (!this._validateData(update, ['userId', 'status'])) {
                console.warn('[PubSubCollaborationService] presence_update - Invalid event at index', index, '- missing required fields');
                return;
              }

              const presenceData: PresenceUpdate = {
                matterId: update?.matterId || update?.MatterId || '',
                userId: update.userId,
                userUID: update?.userUID || this.currentUserId || '',
                status: update.status as PresenceStatus,
                ts: update?.ts,
              };

              console.log('[PubSubCollaborationService] presence_update processed:', { userId: presenceData.userId, status: presenceData.status, matterId: presenceData.matterId });

              // Update internal roster
              if (['active', 'inactive'].includes(presenceData.status)) {
                this.currentRoster[presenceData.userId] = presenceData.status;
                console.debug('[PubSubCollaborationService] presence_update - Triggering', this.presenceCallbacks.length, 'callbacks');
                this.presenceCallbacks.forEach((cb, cbIndex) => {
                  try {
                    cb(presenceData);
                    console.debug('[PubSubCollaborationService] presence_update - Callback', cbIndex, 'executed successfully');
                  } catch (err) {
                    console.error('[PubSubCollaborationService] presence_update - Callback', cbIndex, 'error:', err);
                  }
                });
              }
            });
            break;
          }

          case 'presence_roster': {
            console.log('[PubSubCollaborationService] Processing presence_roster event');
            if (!this._validateData(data, ['roster'])) {
              console.warn('[PubSubCollaborationService] presence_roster - Validation failed: missing roster field');
              break;
            }
            if (typeof data.roster !== 'object') {
              console.warn('[PubSubCollaborationService] presence_roster - Validation failed: roster is not an object');
              break;
            }
            const rosterData: PresenceRoster = {
              matterId: data?.matterId || data?.MatterId || '',
              roster: data.roster,
              userUID: data?.userUID || this.currentUserId || '',
              ts: data?.ts,
            };
            console.log('[PubSubCollaborationService] presence_roster processed:', { matterId: rosterData.matterId, rosterSize: Object.keys(rosterData.roster).length });

            // Update internal roster
            this.currentRoster = { ...data.roster };
            console.debug('[PubSubCollaborationService] presence_roster - Triggering', this.rosterCallbacks.length, 'callbacks');
            this.rosterCallbacks.forEach((cb, cbIndex) => {
              try {
                cb(rosterData);
                console.debug('[PubSubCollaborationService] presence_roster - Callback', cbIndex, 'executed successfully');
              } catch (err) {
                console.error('[PubSubCollaborationService] presence_roster - Callback', cbIndex, 'error:', err);
              }
            });
            break;
          }

          case 'cursor_update': {
            console.log('[PubSubCollaborationService] Processing cursor_update event');
            // Handle both array and single object formats
            const updates = Array.isArray(data) ? data : [data];
            console.debug('[PubSubCollaborationService] cursor_update - Number of events:', updates.length);

            updates.forEach((update: any, index: number) => {
              if (!this._validateData(update, ['userId', 'cursor'])) {
                console.warn('[PubSubCollaborationService] cursor_update - Invalid event at index', index, '- missing required fields');
                return;
              }

              const cursorData: CursorUpdate = {
                matterId: update?.matterId || update?.MatterId || '',
                userId: update.userId,
                userUID: update?.userUID || this.currentUserId || '',
                cursor: update.cursor,
                ts: update?.ts,
              };

              console.log('[PubSubCollaborationService] cursor_update processed:', { userId: cursorData.userId, matterId: cursorData.matterId });

              console.debug('[PubSubCollaborationService] cursor_update - Triggering', this.cursorCallbacks.length, 'callbacks');
              this.cursorCallbacks.forEach((cb, cbIndex) => {
                try {
                  cb(cursorData);
                  console.debug('[PubSubCollaborationService] cursor_update - Callback', cbIndex, 'executed successfully');
                } catch (err) {
                  console.error('[PubSubCollaborationService] cursor_update - Callback', cbIndex, 'error:', err);
                }
              });
            });
            break;
          }

          case 'doc_op': {
            // Handle both array and single object formats
            const updates = Array.isArray(data) ? data : [data];

            updates.forEach((update: any, index: number) => {
              // Determine if ops is an array (batch) or single operation
              const opsArray: any[] = [];

              if (Array.isArray(update?.ops)) {
                // Scenario 1: ops field is an array of operations
                opsArray.push(...update.ops);
              } else if (update?.ops) {
                // Scenario 1b: ops field is a single operation object
                opsArray.push(update.ops);
              } else if (update?.type) {
                // Scenario 2: ops field doesn't exist but operation fields are at root level
                opsArray.push({
                  type: update.type,
                  position: update.position,
                  text: update.text,
                  length: update.length,
                  draftTypeId: update.draftTypeId,
                  timestamp: update.timestamp,
                  userId: update.userId,
                  opId: update.opId,
                  // Yjs CRDT fields - needed for binary sync relay
                  yjsType: update.yjsType,
                  yjsData: update.yjsData,
                  documentId: update.documentId,
                });
              }

              // Validate that we have at least one operation
              if (opsArray.length === 0) {
                console.error('[PubSubCollaborationService] doc_op - INVALID event at index', index);
                console.error('[PubSubCollaborationService] Missing ops field. Raw data:', JSON.stringify(update, null, 2));
                return;
              }

              // Get userId from either field
              const userId = update.userId || opsArray[0]?.userId;
              if (!userId) {
                console.warn('[PubSubCollaborationService] doc_op - Invalid event at index', index, '- missing userId');
                return;
              }

              // Process each operation in the batch in order
              opsArray.forEach((singleOp, opIndex) => {
                const opData: DocumentOp = {
                  matterId: update?.matterId || singleOp?.matterId || '',
                  userId: userId,
                  userUID: update?.userUID || singleOp?.userUID || this.currentUserId || '',
                  opId: update?.opId || singleOp?.opId,
                  ops: singleOp,
                  ts: update?.ts || singleOp?.timestamp,
                };

                // Concise log: show Yjs type if present, otherwise op type
                const logType = opData.ops?.yjsType || opData.ops?.type || 'unknown';
                console.debug(`[PubSubCollab] doc_op recv: user=${opData.userId} type=${logType} callbacks=${this.docOpCallbacks.length}`);

                this.docOpCallbacks.forEach((cb, cbIndex) => {
                  try {
                    cb(opData);
                  } catch (err) {
                    console.error('[PubSubCollaborationService] doc_op callback error:', cbIndex, err);
                  }
                });
              });
            });
            break;
          }

          case 'table_cell_op': {
            console.log('[PubSubCollaborationService] Processing table_cell_op event');
            const cellUpdates = Array.isArray(data) ? data : [data];
            cellUpdates.forEach((update: any) => {
              if (!update || typeof update !== 'object') return;
              const cellPayload = update.data || update;
              if (!cellPayload.tableId || !cellPayload.type) return;

              const cellOp: TableCellOp = {
                matterId: cellPayload.matterId || '',
                tableId: cellPayload.tableId,
                cellId: cellPayload.cellId || '',
                type: cellPayload.type,
                userId: String(cellPayload.userId || ''),
                userName: cellPayload.userName,
                value: cellPayload.value,
                format: cellPayload.format,
                opId: cellPayload.opId,
                ts: cellPayload.ts,
              };
              this.tableCellOpCallbacks.forEach(cb => {
                try { cb(cellOp); } catch (err) {
                  console.error('[PubSubCollaborationService] table_cell_op callback error:', err);
                }
              });
            });
            break;
          }

          default:
            console.debug('[PubSubCollaborationService] Unknown event type received:', eventName);
            break;
        }
      } catch (err) {
        console.error('[PubSubCollaborationService] Unexpected error processing event', eventName, ':', err);
      }
    });

    this.pubsub.on(this.hub, 'disconnect', (reason?: string) => {
      console.warn('[PubSubCollaborationService] Disconnected from hub. Reason:', reason);
      this.notifyConnectionStatus(false);
    });

    // In Socket.IO v4, 'reconnect' fires on the Manager (not the Socket),
    // so socket.on('reconnect') may never fire. Listen for 'connect' instead,
    // which fires on BOTH initial connection AND reconnection.
    this.pubsub.on(this.hub, 'connect', () => {
      console.log('[PubSubCollaborationService] Socket connected/reconnected to hub');
      this.notifyConnectionStatus(true);
    });

    this.pubsub.on(this.hub, 'reconnect', () => {
      console.log('[PubSubCollaborationService] Reconnected to hub (Manager event)');
      this.notifyConnectionStatus(true);
    });

    this.pubsub.on(this.hub, 'connect_error', (err: any) => {
      console.error('[PubSubCollaborationService] Connection error:', err);
      this.notifyConnectionStatus(false);
    });
  }

  joinMatter(matterId: string, userUid?: string): boolean {
    if (!this.isConnected()) {
      console.warn('[PubSubCollaborationService] Cannot join - not connected');
      return false;
    }

    if (!matterId || typeof matterId !== 'string') {
      console.error('[PubSubCollaborationService] Invalid matterId for join');
      return false;
    }

    const uid = userUid || this.currentUserId;
    if (!uid) {
      console.error('[PubSubCollaborationService] No user ID available for matter join');
      return false;
    }

    console.log('[PubSubCollaborationService] Emitting matter_collab_join_request:', { matterId, userUID: uid });
    const emitResult = this.pubsub.emit(this.hub, 'matter_collab_join_request', {
      matterId: matterId,
      userUID: uid,
    });
    console.debug('[PubSubCollaborationService] matter_collab_join_request emit result:', emitResult);
    return emitResult;
  }

  leaveDocument(matterId: string): boolean {
    if (!this.isConnected()) {
      console.warn('[PubSubCollaborationService] Cannot leave - not connected');
      return false;
    }

    if (!matterId || typeof matterId !== 'string') {
      console.error('[PubSubCollaborationService] Invalid matterId for leave');
      return false;
    }

    console.log('[PubSubCollaborationService] Emitting collab_leave:', { matterId, userUID: this.currentUserId });
    const emitResult = this.pubsub.emit(this.hub, 'collab_leave', {
      matterId: String(matterId),
      userUID: this.currentUserId || '',
    });
    console.debug('[PubSubCollaborationService] collab_leave emit result:', emitResult);
    return emitResult;
  }

  leaveMatter(matterId: string): boolean {
    if (!this.isConnected()) {
      console.warn('[PubSubCollaborationService] Cannot leave - not connected');
      return false;
    }

    if (!matterId || typeof matterId !== 'string') {
      console.error('[PubSubCollaborationService] Invalid matterId for leave');
      return false;
    }

    console.log('[PubSubCollaborationService] Emitting collab_leave via leaveMatter:', { matterId, userUID: this.currentUserId });
    const emitResult = this.pubsub.emit(this.hub, 'collab_leave', {
      matterId: String(matterId),
      userUID: this.currentUserId || '',
    });
    console.debug('[PubSubCollaborationService] collab_leave emit result:', emitResult);
    return emitResult;
  }

  sendPresencePing(matterId: string): boolean {
    if (!this.isConnected()) {
      console.warn('[PubSubCollaborationService] Cannot send presence - not connected');
      return false;
    }

    if (!matterId || typeof matterId !== 'string') {
      console.error('[PubSubCollaborationService] Invalid matterId for presence ping');
      return false;
    }

    console.log('[PubSubCollaborationService] Emitting presence_ping:', { matterId, userUID: this.currentUserId });
    const emitResult = this.pubsub.emit(this.hub, 'presence_ping', {
      matterId: String(matterId),
      userUID: this.currentUserId || '',
    });
    console.debug('[PubSubCollaborationService] presence_ping emit result:', emitResult);
    return emitResult;
  }

  sendCursorUpdate(matterId: string, cursor: any): boolean {
    if (!this.isConnected()) {
      console.warn('[PubSubCollaborationService] Cannot send cursor - not connected');
      return false;
    }

    if (!matterId || !cursor) {
      console.error('[PubSubCollaborationService] Invalid matterId or cursor for update');
      return false;
    }

    console.log('[PubSubCollaborationService] Emitting cursor_update:', { matterId, userUID: this.currentUserId, cursorKeys: Object.keys(cursor || {}) });
    const emitResult = this.pubsub.emit(this.hub, 'cursor_update', {
      matterId: String(matterId),
      UserUID: this.currentUserId || '',
      cursor,
    });
    console.debug('[PubSubCollaborationService] cursor_update emit result:', emitResult);
    return emitResult;
  }

  sendDocumentOp(matterId: string, ops: any, opId?: string): boolean {
    if (!this.isConnected()) {
      console.warn('[PubSubCollaborationService] Cannot send op - not connected');
      return false;
    }

    if (!matterId || !ops) {
      console.error('[PubSubCollaborationService] Invalid matterId or ops for update');
      return false;
    }

    // Handle both single operation and array of operations
    const isArray = Array.isArray(ops);
    const opsToSend = isArray ? ops : [ops];

    // Process and send each operation individually as a flat structure
    // The server expects flat operation format with fields at root level
    for (let i = 0; i < opsToSend.length; i++) {
      const op = opsToSend[i];

      if (!op || typeof op !== 'object') {
        console.warn('[PubSubCollaborationService] Skipping invalid operation:', op);
        continue;
      }

      // Validate required fields exist and are not null/undefined
      if (!op.type) {
        console.error('[PubSubCollaborationService] Operation missing type field:', op);
        continue;
      }

      if (op.position === null || op.position === undefined) {
        console.error('[PubSubCollaborationService] Operation missing position field:', op);
        continue;
      }

      if (!op.userId) {
        console.error('[PubSubCollaborationService] Operation missing userId field:', op);
        continue;
      }

      // Build FLAT operation payload (fields at root level, as expected by server)
      // This is the crucial fix - server expects flat structure, not nested
      const payload: Record<string, any> = {
        matterId: String(matterId),
        userId: op.userId,
        userUID: op.userUID || this.currentUserId || '',
        draftTypeId: op.draftTypeId || '',
        type: op.type, // CRITICAL: At root level, not nested
        position: op.position, // CRITICAL: At root level, not nested
        ...(op.text !== undefined && { text: op.text }),
        ...(op.length !== undefined && { length: op.length }),
        timestamp: op.timestamp || new Date().toISOString(),
        opId: op.opId || opId || '',
        // Yjs CRDT fields - passed through for binary sync relay
        ...(op.yjsType && { yjsType: op.yjsType }),
        ...(op.yjsData && { yjsData: op.yjsData }),
        ...(op.documentId && { documentId: op.documentId }),
      };

      const logType = payload.yjsType || payload.type;
      console.debug(`[PubSubCollab] doc_op send: user=${payload.userId} type=${logType} doc=${payload.documentId || ''}`);

      const emitResult = this.pubsub.emit(this.hub, 'doc_op', payload);

      if (emitResult) {
        // success - no need to log every emit
      } else {
        console.warn(`[PubSubCollaborationService] doc_op emit failed for type=${logType}`);
        return false;
      }
    }

    return true;
  }

  /**
   * Send a table cell collaboration operation (edit, select, deselect, format).
   * Uses the 'table_cell_op' event on the collaboration hub.
   */
  sendTableCellOp(
    matterId: string,
    tableId: string,
    cellId: string,
    type: 'cell_edit' | 'cell_select' | 'cell_deselect' | 'cell_format',
    data?: { value?: string; format?: Record<string, any>; userName?: string; opId?: string }
  ): boolean {
    if (!this.isConnected()) {
      console.warn('[PubSubCollaborationService] Cannot send table_cell_op - not connected');
      return false;
    }

    if (!matterId || !tableId) {
      console.error('[PubSubCollaborationService] Invalid matterId or tableId for table_cell_op');
      return false;
    }

    const payload = {
      matterId: String(matterId),
      tableId: String(tableId),
      cellId: cellId || '',
      type,
      UserUID: this.currentUserId || '',
      userName: data?.userName || '',
      value: data?.value,
      format: data?.format,
      opId: data?.opId || '',
    };

    console.log('[PubSubCollaborationService] Emitting table_cell_op:', { matterId, tableId, cellId, type });
    return this.pubsub.emit(this.hub, 'table_cell_op', payload);
  }

  // ------------------------------------------------------------------
  // Subscriptions
  // ------------------------------------------------------------------

  onJoinOk(cb: (d: JoinOkPayload) => void) {
    this.joinOkCallbacks.push(cb);
    console.log('[PubSubCollaborationService] Subscribed to collab_join_ok. Total subscribers:', this.joinOkCallbacks.length);
    return () => {
      this._off(this.joinOkCallbacks, cb);
      console.log('[PubSubCollaborationService] Unsubscribed from collab_join_ok. Remaining subscribers:', this.joinOkCallbacks.length);
    };
  }

  onJoinDenied(cb: (d: { matterId: string; userUID?: string }) => void) {
    this.joinDeniedCallbacks.push(cb);
    console.log('[PubSubCollaborationService] Subscribed to collab_join_denied. Total subscribers:', this.joinDeniedCallbacks.length);
    return () => {
      this._off(this.joinDeniedCallbacks, cb);
      console.log('[PubSubCollaborationService] Unsubscribed from collab_join_denied. Remaining subscribers:', this.joinDeniedCallbacks.length);
    };
  }

  onPresence(cb: (d: PresenceUpdate) => void) {
    this.presenceCallbacks.push(cb);
    console.log('[PubSubCollaborationService] Subscribed to presence_update. Total subscribers:', this.presenceCallbacks.length);
    return () => {
      this._off(this.presenceCallbacks, cb);
      console.log('[PubSubCollaborationService] Unsubscribed from presence_update. Remaining subscribers:', this.presenceCallbacks.length);
    };
  }

  onRoster(cb: (d: PresenceRoster) => void) {
    this.rosterCallbacks.push(cb);
    console.log('[PubSubCollaborationService] Subscribed to presence_roster. Total subscribers:', this.rosterCallbacks.length);
    return () => {
      this._off(this.rosterCallbacks, cb);
      console.log('[PubSubCollaborationService] Unsubscribed from presence_roster. Remaining subscribers:', this.rosterCallbacks.length);
    };
  }

  onCursor(cb: (d: CursorUpdate) => void) {
    this.cursorCallbacks.push(cb);
    console.log('[PubSubCollaborationService] Subscribed to cursor_update. Total subscribers:', this.cursorCallbacks.length);
    return () => {
      this._off(this.cursorCallbacks, cb);
      console.log('[PubSubCollaborationService] Unsubscribed from cursor_update. Remaining subscribers:', this.cursorCallbacks.length);
    };
  }

  onDocOp(cb: (d: DocumentOp) => void) {
    this.docOpCallbacks.push(cb);
    console.log('[PubSubCollaborationService] Subscribed to doc_op. Total subscribers:', this.docOpCallbacks.length);
    return () => {
      this._off(this.docOpCallbacks, cb);
      console.log('[PubSubCollaborationService] Unsubscribed from doc_op. Remaining subscribers:', this.docOpCallbacks.length);
    };
  }

  onTableCellOp(cb: (d: TableCellOp) => void) {
    this.tableCellOpCallbacks.push(cb);
    console.log('[PubSubCollaborationService] Subscribed to table_cell_op. Total subscribers:', this.tableCellOpCallbacks.length);
    return () => {
      this._off(this.tableCellOpCallbacks, cb);
      console.log('[PubSubCollaborationService] Unsubscribed from table_cell_op. Remaining subscribers:', this.tableCellOpCallbacks.length);
    };
  }

  onConnectionStatusChange(cb: (connected: boolean) => void) {
    this.connectionStatusCallbacks.push(cb);
    console.log('[PubSubCollaborationService] Subscribed to connectionStatusChange. Total subscribers:', this.connectionStatusCallbacks.length);
    return () => {
      this._off(this.connectionStatusCallbacks, cb);
      console.log('[PubSubCollaborationService] Unsubscribed from connectionStatusChange. Remaining subscribers:', this.connectionStatusCallbacks.length);
    };
  }

  private notifyConnectionStatus(connected: boolean) {
    console.log('[PubSubCollaborationService] Connection status changed:', connected, '- Notifying', this.connectionStatusCallbacks.length, 'subscribers');
    this.connectionStatusCallbacks.forEach((cb, index) => {
      try {
        cb(connected);
        console.debug('[PubSubCollaborationService] Connection status callback', index, 'executed successfully');
      } catch (err) {
        console.error('[PubSubCollaborationService] Connection status callback', index, 'error:', err);
      }
    });
  }

  private _off<T>(arr: T[], cb: T) {
    const i = arr.indexOf(cb);
    if (i > -1) arr.splice(i, 1);
  }

  isConnected(): boolean {
    const connected = this.pubsub.isConnected(this.hub);
    console.debug('[PubSubCollaborationService] isConnected check:', connected);
    return connected;
  }

  getCurrentRoster(): CollaborativeUser[] {
    const roster = Object.entries(this.currentRoster).map(([userId, status]) => ({
      userId,
      status,
    }));
    console.debug('[PubSubCollaborationService] getCurrentRoster:', { count: roster.length, roster });
    return roster;
  }

  getActiveUsers(): string[] {
    const activeUsers = Object.entries(this.currentRoster)
      .filter(([, status]) => status === 'active')
      .map(([userId]) => userId);
    console.debug('[PubSubCollaborationService] getActiveUsers:', { count: activeUsers.length, users: activeUsers });
    return activeUsers;
  }

  getCurrentGroup(): string | null {
    console.debug('[PubSubCollaborationService] getCurrentGroup:', this.currentGroup);
    return this.currentGroup;
  }

  disconnect(): void {
    console.log('[PubSubCollaborationService] Disconnecting from hub');
    console.debug('[PubSubCollaborationService] Current state before disconnect:', { group: this.currentGroup, rosterSize: Object.keys(this.currentRoster).length });
    this.currentGroup = null;
    this.currentRoster = {};
    this.listenersInitialized = false; // Reset flag so listeners can be re-attached on reconnection
    this.pubsub.disconnectHub(this.hub);
    console.log('[PubSubCollaborationService] Disconnected and state cleared');
  }

  /**
   * Rejoin a matter after disconnection or navigation
   * Useful when returning to a matter that was previously joined
   */
  rejoinMatter(matterId: string, userUid?: string): boolean {
    console.log('[PubSubCollaborationService] Attempting to rejoin matter:', { matterId, userUID: userUid || this.currentUserId });

    if (!matterId) {
      console.error('[PubSubCollaborationService] Invalid matterId for rejoin');
      return false;
    }

    // If not connected, initialize first
    if (!this.isConnected()) {
      const uid = userUid || this.currentUserId;
      if (!uid) {
        console.error('[PubSubCollaborationService] No user ID available for matter rejoin');
        return false;
      }

      console.log('[PubSubCollaborationService] Not connected, reconnecting before rejoin');
      // Reconnect and rejoin
      return this.joinMatter(matterId, uid);
    }

    console.log('[PubSubCollaborationService] Already connected, proceeding with rejoin');
    // Already connected, just rejoin
    return this.joinMatter(matterId, userUid);
  }
}

// Singleton
let collabService: PubSubCollaborationService | null = null;

export const getCollaborationService = (userId?: string) => {
  if (!collabService) {
    console.log('[PubSubCollaborationService] Creating new singleton instance with userId:', userId);
    collabService = new PubSubCollaborationService(userId);
  } else {
    console.debug('[PubSubCollaborationService] Returning existing singleton instance');
  }
  return collabService;
};

export default PubSubCollaborationService;
